/** Rechnungsarten (E-Rechnung Paket 3): Anzeige, Aktionsregeln und Vorschau-Rechnungen.
    Reine Logik ohne React — Beträge werden in Cent (ganze Zahlen) gerechnet, nie als float. */
import type { Rechnungsart } from '../types';

export const RECHNUNGSARTEN: Rechnungsart[] = ['RECHNUNG', 'ABSCHLAG', 'SCHLUSS', 'GUTSCHRIFT', 'STORNO'];

const ART_LABEL: Record<Rechnungsart, string> = {
  RECHNUNG: 'Rechnung',
  ABSCHLAG: 'Abschlagsrechnung',
  SCHLUSS: 'Schlussrechnung',
  GUTSCHRIFT: 'Gutschrift',
  STORNO: 'Stornorechnung',
};

/** Fehlende oder unbekannte Art gilt als normale Rechnung (Altbestand). */
export function normalisiereArt(art?: string | null): Rechnungsart {
  const a = (art ?? '').toUpperCase();
  return (RECHNUNGSARTEN as string[]).includes(a) ? (a as Rechnungsart) : 'RECHNUNG';
}

export function artLabel(art?: string | null): string {
  return ART_LABEL[normalisiereArt(art)];
}

/** Gutschrift und Stornorechnung sind Korrekturbelege: Betrag mindert die Forderung. */
export function istKorrektur(art?: string | null): boolean {
  const a = normalisiereArt(art);
  return a === 'GUTSCHRIFT' || a === 'STORNO';
}

/** CSS-Modifier für das Art-Badge. */
export function artKlasse(art?: string | null): 'normal' | 'abschlag' | 'schluss' | 'korrektur' {
  switch (normalisiereArt(art)) {
    case 'ABSCHLAG': return 'abschlag';
    case 'SCHLUSS': return 'schluss';
    case 'GUTSCHRIFT':
    case 'STORNO': return 'korrektur';
    default: return 'normal';
  }
}

// ---------------------------------------------------------------------------
// Beträge in Cent
// ---------------------------------------------------------------------------

/** Zahl oder Text ("150.50", "1.234,56", "150,5", "1.200") -> Cent; null bei unlesbarer Eingabe.
    Mehr als zwei Nachkommastellen werden kaufmännisch gerundet. */
export function inCent(wert: string | number | null | undefined): number | null {
  if (wert === null || wert === undefined) return null;
  let text = typeof wert === 'number' ? (Number.isFinite(wert) ? wert.toFixed(2) : '') : String(wert).trim();
  if (!text) return null;
  // "1.200" (nur Dreiergruppen) ist deutsch 1200, nicht 1,2; API-Strings haben nie drei Nachkommastellen.
  if (text.includes(',') || /^-?\d{1,3}(\.\d{3})+$/.test(text)) text = text.replace(/\./g, '').replace(',', '.');
  const m = /^(-)?(\d+)(?:\.(\d+))?$/.exec(text);
  if (!m) return null;
  const frac = (m[3] ?? '').padEnd(3, '0');
  let cent = parseInt(m[2], 10) * 100 + parseInt(frac.slice(0, 2), 10);
  if (parseInt(frac[2], 10) >= 5) cent += 1;
  return m[1] ? -cent : cent;
}

/** Cent -> "1.234,56 €" (negativ mit echtem Minuszeichen). */
export function centText(cent: number): string {
  const text = `${(Math.abs(cent) / 100).toLocaleString('de-DE', {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  return cent < 0 ? `−${text}` : text;
}

/** Cent -> API-Dezimalstring "150.50" (Decimal-Felder des Backends). */
export function centAlsDezimal(cent: number): string {
  const vz = cent < 0 ? '-' : '';
  const a = Math.abs(cent);
  return `${vz}${Math.floor(a / 100)}.${String(a % 100).padStart(2, '0')}`;
}

/** Nutzereingabe einer Dezimalzahl ("33,5") -> "33.5" für die API; null bei Unlesbarem oder ≤ 0. */
export function alsDezimalString(eingabe: string): string | null {
  const cent = inCent(eingabe);
  if (cent === null || cent <= 0) return null;
  return centAlsDezimal(cent);
}

/** Prozent einer Basis, kaufmännisch gerundet: basisCent × (promille100 / 10000), ganzzahlig.
    `hundertstelProzent` = Prozentwert × 100 (33,33 % -> 3333). */
export function anteilCent(basisCent: number, hundertstelProzent: number): number {
  return Math.floor((basisCent * hundertstelProzent * 2 + 10000) / 20000);
}

/** Betrag der Rechnung mit Vorzeichen für die Anzeige: Gutschrift/Storno sind positiv
    gespeichert und werden als Abzug ("−119,00 €") gezeigt. */
export function betragAnzeige(brutto: number | null | undefined, art?: string | null): string {
  const cent = inCent(brutto);
  if (cent === null) return '–';
  return centText(istKorrektur(art) ? -Math.abs(cent) : cent);
}

// ---------------------------------------------------------------------------
// Aktionsregeln
// ---------------------------------------------------------------------------

interface RechnungKurz {
  rechnungsart?: string | null;
  status?: string | null;
  zahlbetrag?: number | null;
}

const STORNIERBAR_STATUS = ['VERSENDET', 'UEBERFAELLIG', 'TEILBEZAHLT'];
const GUTSCHRIFTFAEHIG_STATUS = ['VERSENDET', 'UEBERFAELLIG', 'TEILBEZAHLT', 'BEZAHLT'];

/** Storno-Button: nur festgeschriebene, nicht bezahlte Rechnungen (Entwürfe werden verworfen,
    Korrekturbelege lassen sich nicht stornieren). Eine voll durch Abschläge gedeckte Schluss-
    rechnung (Zahlbetrag 0, Status BEZAHLT) ist stornierbar — sofern der Zahlbetrag bekannt ist. */
export function kannStornieren(r: RechnungKurz): boolean {
  if (istKorrektur(r.rechnungsart)) return false;
  if (STORNIERBAR_STATUS.includes(r.status ?? '')) return true;
  return normalisiereArt(r.rechnungsart) === 'SCHLUSS' && r.status === 'BEZAHLT' && r.zahlbetrag === 0;
}

/** Gutschrift-Button: nur festgeschriebene, nicht stornierte Rechnungen, keine Korrekturbelege und
    keine Abschlagsrechnungen — die werden per Storno oder in der Schlussrechnung korrigiert (ein
    gutgeschriebener Abschlag wäre nie mehr verrechenbar; der Server lehnt mit 409 ab). */
export function kannGutschreiben(r: RechnungKurz): boolean {
  if (istKorrektur(r.rechnungsart)) return false;
  if (normalisiereArt(r.rechnungsart) === 'ABSCHLAG') return false;
  return GUTSCHRIFTFAEHIG_STATUS.includes(r.status ?? '');
}

/** Bezugstext der Liste ("zu RE-2026-0004"); ohne Nummer Rückfall auf die Id. */
export function bezugText(r: {
  rechnungsart?: string | null; bezug_rechnung_id?: number | null; bezug_rechnungsnummer?: string | null;
}, nummerZuId?: (id: number) => string | undefined): string | null {
  if (!istKorrektur(r.rechnungsart)) return null;
  const nr = r.bezug_rechnungsnummer
    || (r.bezug_rechnung_id != null ? nummerZuId?.(r.bezug_rechnung_id) : undefined);
  if (nr) return `zu ${nr}`;
  return r.bezug_rechnung_id != null ? `zu Rechnung #${r.bezug_rechnung_id}` : null;
}

// ---------------------------------------------------------------------------
// Abschlag
// ---------------------------------------------------------------------------

export interface AbschlagVorschau {
  nettoCent: number;
  mwstCent: number;
  bruttoCent: number;
  /** Noch nicht per Abschlag abgerechnetes Netto nach diesem Abschlag; null ohne bekannte Auftragssumme. */
  restNettoCent: number | null;
  /** Der Abschlag übersteigt den noch offenen Auftragswert (der Server lehnt das ab). */
  uebersteigt: boolean;
}

export interface AbschlagEingabe {
  modus: 'betrag' | 'prozent';
  eingabe: string;
  /** Netto der Auftragspositionen nach Gesamtrabatt in Cent; null = unbekannt. */
  basisNettoCent: number | null;
  /** Netto bisheriger (nicht stornierter) Abschläge in Cent. */
  bisherNettoCent: number;
  /** USt-Satz in Prozent, z. B. 19. */
  satz: number;
}

/** Vorschau eines Abschlags; null, solange die Eingabe nicht lesbar/positiv ist
    (bzw. bei Prozent ohne bekannte Auftragssumme). Die endgültige Steuer ergibt sich aus
    den Auftragspositionen — hier wird mit einem einheitlichen Satz gerechnet. */
export function abschlagVorschau(e: AbschlagEingabe): AbschlagVorschau | null {
  const wert = inCent(e.eingabe);
  if (wert === null || wert <= 0) return null;
  let netto: number;
  if (e.modus === 'prozent') {
    if (e.basisNettoCent === null || wert > 10000) return null;
    netto = anteilCent(e.basisNettoCent, wert);
  } else {
    netto = wert;
  }
  const mwst = anteilCent(netto, Math.round(e.satz * 100));
  const rest = e.basisNettoCent === null ? null : e.basisNettoCent - e.bisherNettoCent - netto;
  return {
    nettoCent: netto,
    mwstCent: mwst,
    bruttoCent: netto + mwst,
    restNettoCent: rest,
    uebersteigt: rest !== null && rest < 0,
  };
}

/** Netto der Auftragspositionen nach Gesamtrabatt in Cent (ohne Metallzuschläge); null, wenn
    die Summe fehlt/0 ist (z. B. ohne Recht „Preise sehen“). */
export function auftragNettoCent(
  gesamtsumme: number | null | undefined, gesamtrabattProzent?: number | null,
): number | null {
  const summe = inCent(gesamtsumme);
  if (summe === null || summe <= 0) return null;
  const rabatt = inCent(gesamtrabattProzent ?? 0) ?? 0;
  return summe - anteilCent(summe, Math.max(0, Math.min(10000, rabatt)));
}

interface AbschlagKandidat {
  ausgangsrechnung_id: number;
  rechnungsart?: string | null;
  status?: string | null;
  bezug_rechnung_id?: number | null;
  nummer_anzeige?: string;
}

export interface AbschlagOption<T extends AbschlagKandidat> {
  rechnung: T;
  auswaehlbar: boolean;
  /** Grund, warum der Abschlag nicht wählbar ist (sonst null). */
  grund: string | null;
}

/** Abschlagsrechnungen des Auftrags als Checkliste für die Schlussrechnung. Wählbar sind nur
    bezahlte/teilbezahlte Abschläge ohne Gutschrift (Regeln des Servers; der Server prüft
    zusätzlich, ob ein Abschlag schon in einer anderen Schlussrechnung verrechnet ist). */
export function abschlagOptionen<T extends AbschlagKandidat>(rechnungen: T[]): AbschlagOption<T>[] {
  return rechnungen
    .filter(r => normalisiereArt(r.rechnungsart) === 'ABSCHLAG')
    .map(r => {
      const gutschriften = rechnungen.filter(g =>
        normalisiereArt(g.rechnungsart) === 'GUTSCHRIFT'
        && g.bezug_rechnung_id === r.ausgangsrechnung_id
        && g.status !== 'STORNIERT');
      let grund: string | null = null;
      switch (r.status) {
        case 'BEZAHLT':
        case 'TEILBEZAHLT':
          if (gutschriften.length > 0) {
            grund = `Gutschrift vorhanden (${gutschriften.map(g => g.nummer_anzeige ?? `#${g.ausgangsrechnung_id}`).join(', ')}) – erst korrigieren, dann verrechnen`;
          }
          break;
        case 'STORNIERT': grund = 'storniert'; break;
        case 'ENTWURF': grund = 'noch ein Entwurf – erst festschreiben'; break;
        case 'VERSENDET':
        case 'UEBERFAELLIG': grund = 'noch nicht bezahlt'; break;
        default: grund = 'Status erlaubt keine Verrechnung';
      }
      return { rechnung: r, auswaehlbar: grund === null, grund };
    });
}

/** Abschläge, die eine Schlussrechnung mit dieser Auswahl liegen ließe: nicht storniert und nicht
    gewählt. Die Schlussrechnung berechnet die volle Leistung — solche Abschläge blieben zusätzlich
    offen (Doppelabrechnung); der Server lehnt deshalb mit 409 ab. Gibt es schon eine nicht stornierte
    Schlussrechnung, kann die Liste nicht erkennen, welche Abschläge dort verrechnet sind — dann
    entscheidet der Server (leere Rückgabe). */
export function liegenGelasseneAbschlaege<T extends AbschlagKandidat>(rechnungen: T[], gewaehlt: Set<number>): T[] {
  const andereSchluss = rechnungen.some(r => normalisiereArt(r.rechnungsart) === 'SCHLUSS' && r.status !== 'STORNIERT');
  if (andereSchluss) return [];
  return rechnungen.filter(r => normalisiereArt(r.rechnungsart) === 'ABSCHLAG'
    && r.status !== 'STORNIERT' && !gewaehlt.has(r.ausgangsrechnung_id));
}

export interface ZahlbetragVorschau {
  gesamtBruttoCent: number;
  abzugBruttoCent: number;
  zahlbetragCent: number;
}

/** „Zahlbetrag nach Abzug“ einer Schlussrechnung: Gesamtleistung (brutto, mit einheitlichem Satz
    gerechnet) abzüglich der gewählten Abschläge (brutto, wie in der Rechnung ausgewiesen). */
export function zahlbetragVorschau(
  basisNettoCent: number | null, satz: number, gewaehlteAbschlaegeBrutto: Array<number | null | undefined>,
): ZahlbetragVorschau | null {
  if (basisNettoCent === null) return null;
  const gesamt = basisNettoCent + anteilCent(basisNettoCent, Math.round(satz * 100));
  const abzug = gewaehlteAbschlaegeBrutto.reduce<number>((s, b) => s + (inCent(b) ?? 0), 0);
  return { gesamtBruttoCent: gesamt, abzugBruttoCent: abzug, zahlbetragCent: gesamt - abzug };
}

// ---------------------------------------------------------------------------
// Pflicht-Hinweis (Unternehmerkunde ohne E-Rechnungsformat)
// ---------------------------------------------------------------------------

export interface PflichtHinweisEingabe {
  unternehmer?: boolean | null;
  eRechnungFormat?: string | null;
  landCode?: string | null;
  /** Wirksamer Stichtag (ISO) aus den Firmenstammdaten; null = nicht bekannt/lesbar. */
  stichtag: string | null;
  kleinunternehmer?: boolean | null;
  /** Heute als ISO-Datum (JJJJ-MM-TT). */
  heute: string;
}

/** Nicht blockierender Hinweis beim Erstellen: Der Kunde ist Unternehmer im Inland, hat kein
    E-Rechnungsformat und der Pflicht-Stichtag ist erreicht. Ohne bekannten Stichtag (Firmendaten
    nicht lesbar) wird nichts behauptet. */
export function pflichtHinweisAktiv(e: PflichtHinweisEingabe): boolean {
  if (e.unternehmer !== true) return false;
  if (e.kleinunternehmer) return false;
  const format = (e.eRechnungFormat ?? 'KEINE').toUpperCase();
  if (format !== 'KEINE') return false;
  const land = (e.landCode ?? '').toUpperCase();
  if (land !== '' && land !== 'DE') return false;
  if (!e.stichtag) return false;
  return e.stichtag.slice(0, 10) <= e.heute;
}

export function datumDe(iso?: string | null): string {
  if (!iso) return '';
  const t = iso.slice(0, 10).split('-');
  return t.length === 3 ? `${t[2]}.${t[1]}.${t[0]}` : iso;
}

// ---------------------------------------------------------------------------
// Beleg-Ansicht
// ---------------------------------------------------------------------------

/** Bezugszeile unter der Adresse (wie im PDF): Gutschrift korrigiert, Stornorechnung hebt auf. */
export function bezugZeile(
  rechnungsart: string | null | undefined, bezug: { nummer: string; datum: string | null } | null | undefined,
): string | null {
  if (!bezug?.nummer) return null;
  const am = bezug.datum ? ` vom ${datumDe(bezug.datum)}` : '';
  switch (normalisiereArt(rechnungsart)) {
    case 'GUTSCHRIFT': return `Diese Gutschrift korrigiert die Rechnung Nr. ${bezug.nummer}${am}.`;
    case 'STORNO': return `Diese Stornorechnung hebt die Rechnung Nr. ${bezug.nummer}${am} in vollem Umfang auf.`;
    default: return null;
  }
}

/** Teilt den Summenblock einer Schlussrechnung vor der Zeile „Abzüglich Abschläge“: davor steht die
    Gesamtleistung, danach Abzug und Zahlbetrag — dazwischen die Tabelle der verrechneten Abschläge. */
export function summenTeilen<T extends { art: string }>(zeilen: T[], mitAbschlagstabelle: boolean): { vor: T[]; nach: T[] } {
  const i = mitAbschlagstabelle ? zeilen.findIndex(z => z.art === 'abschlaege') : -1;
  return i < 0 ? { vor: zeilen, nach: [] } : { vor: zeilen.slice(0, i), nach: zeilen.slice(i) };
}

// ---------------------------------------------------------------------------
// Gutschrift: Positionen der Bezugsrechnung
// ---------------------------------------------------------------------------

/** Menge in Tausendstel ("2,5" -> 2500); null bei Unlesbarem. */
export function mengeMilli(wert: string | number | null | undefined): number | null {
  if (wert === null || wert === undefined) return null;
  let text = typeof wert === 'number' ? String(wert) : wert.trim();
  if (!text) return null;
  if (text.includes(',')) text = text.replace(/\./g, '').replace(',', '.');
  const m = /^(\d+)(?:\.(\d+))?$/.exec(text);
  if (!m) return null;
  const frac = (m[2] ?? '').padEnd(4, '0');
  let milli = parseInt(m[1], 10) * 1000 + parseInt(frac.slice(0, 3), 10);
  if (parseInt(frac[3], 10) >= 5) milli += 1;
  return milli;
}

/** Tausendstel -> API-Dezimalstring ("2500" -> "2.5"). */
export function milliAlsDezimal(milli: number): string {
  const a = Math.abs(milli);
  const frac = String(a % 1000).padStart(3, '0').replace(/0+$/, '');
  return `${milli < 0 ? '-' : ''}${Math.floor(a / 1000)}${frac ? `.${frac}` : ''}`;
}

export interface GutschriftPosition {
  /** 0-basierter Index in den Positionen der Bezugsrechnung (Eingabeformat des Servers). */
  index: number;
  bezeichnung: string;
  mengeText: string;
  einheit: string;
  mengeMilli: number | null;
  gesamtCent: number | null;
}

interface AnsichtPositionen {
  titel?: Array<{ positionen?: Array<{
    art?: string; nummer: string; bezeichnung: string; menge: string; einheit: string;
    gesamtpreis?: string | null;
  }> }>;
}

/** Positionen der Bezugsrechnung aus der Beleg-Ansicht (Nummer n = Index n−1 im Snapshot). */
export function gutschriftPositionen(ansicht: AnsichtPositionen): GutschriftPosition[] {
  const result: GutschriftPosition[] = [];
  for (const titel of ansicht.titel ?? []) {
    for (const p of titel.positionen ?? []) {
      if (p.art && p.art !== 'position') continue;
      const nr = parseInt(p.nummer, 10);
      if (!Number.isFinite(nr) || nr < 1) continue;
      result.push({
        index: nr - 1,
        bezeichnung: p.bezeichnung,
        mengeText: p.menge,
        einheit: p.einheit,
        mengeMilli: mengeMilli(p.menge),
        gesamtCent: inCent(p.gesamtpreis),
      });
    }
  }
  return result;
}

export interface GutschriftAuswahl {
  gewaehlt: boolean;
  /** Leer = die ganze (noch offene) Menge. */
  menge: string;
}

/** Netto-Betrag einer Gutschrift-Position: anteilig vom Positionsbetrag; ohne Menge die ganze Position.
    null, wenn die Menge nicht lesbar ist oder die Positionsmenge überschreitet. */
export function gutschriftPositionCent(pos: GutschriftPosition, mengeEingabe: string): number | null {
  if (pos.gesamtCent === null) return null;
  if (!mengeEingabe.trim()) return pos.gesamtCent;
  const m = mengeMilli(mengeEingabe);
  if (m === null || m <= 0 || pos.mengeMilli === null || pos.mengeMilli <= 0 || m > pos.mengeMilli) return null;
  return Math.floor((pos.gesamtCent * m * 2 + pos.mengeMilli) / (pos.mengeMilli * 2));
}

export interface GutschriftBerechnung {
  /** Request-Positionen für `gutschrift_positionen`. */
  positionen: Array<{ index: number; menge?: string }>;
  nettoCent: number;
  /** Positionen mit unlesbarer/zu großer Menge. */
  ungueltig: number[];
}

export function gutschriftBerechnen(
  positionen: GutschriftPosition[], auswahl: Record<number, GutschriftAuswahl>,
): GutschriftBerechnung {
  const out: GutschriftBerechnung = { positionen: [], nettoCent: 0, ungueltig: [] };
  for (const p of positionen) {
    const a = auswahl[p.index];
    if (!a?.gewaehlt) continue;
    const cent = gutschriftPositionCent(p, a.menge);
    if (cent === null) { out.ungueltig.push(p.index); continue; }
    const milli = a.menge.trim() ? mengeMilli(a.menge) : null;
    out.positionen.push(milli !== null ? { index: p.index, menge: milliAlsDezimal(milli) } : { index: p.index });
    out.nettoCent += cent;
  }
  return out;
}
